import { createContext, createElement, useContext, useEffect, useState, type ReactNode } from 'react'

/**
 * The same 1023px line `styles.css` switches density on.
 *
 * A media query in JavaScript rather than an `lg:` class, because below it the
 * job card is a *different tree* and not the same tree restyled: the phone puts
 * the activity feed in a sheet and the desktop puts it in a column, and drawing
 * both would mount the feed twice — every note, photo and quote number in the
 * document twice over, which is a trap for anything reading the page and for
 * anything testing it.
 *
 * Reach for a `lg:` class first. This is for the cases where the two layouts
 * cannot both exist.
 */
const NARROW = '(max-width: 1023px)'

/**
 * The field app: a phone, or any screen whose main pointer is a finger.
 *
 * An iPad in landscape is 1180px wide, which is a desk by width alone, and it
 * is still held by somebody in a driveway. So the field layout is chosen by
 * the pointer as well as the width, and a laptop with a mouse keeps the board.
 * `styles.css` declares the `field:` and `desk:` variants on this same text;
 * change one and change the other.
 */
const FIELD = '(max-width: 1023px), (pointer: coarse)'

/** A field screen with room for a list beside the job: the iPad. */
const TABLET = '(min-width: 1024px) and (pointer: coarse)'

function read(query: string): boolean {
  if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') return false
  return window.matchMedia(query).matches
}

function useMedia(query: string): boolean {
  const [matches, setMatches] = useState(() => read(query))

  useEffect(() => {
    if (typeof window.matchMedia !== 'function') return

    const list = window.matchMedia(query)
    const sync = () => setMatches(list.matches)

    // Re-read on mount as well as on change: the first render may have happened
    // before the window settled at the size it is now.
    sync()
    list.addEventListener('change', sync)
    return () => list.removeEventListener('change', sync)
  }, [query])

  return matches
}

/**
 * A container that is narrow whatever the window is.
 *
 * The iPad's job pane is 580px inside a 1180px screen. What is drawn in it —
 * the billing tab above all — has to take its stacked, phone shape, and the
 * window's width cannot say so.
 */
const NarrowContainer = createContext(false)

export function NarrowProvider({ children }: { children: ReactNode }) {
  return createElement(NarrowContainer.Provider, { value: true }, children)
}

export function useIsNarrow(): boolean {
  const container = useContext(NarrowContainer)
  const narrow = useMedia(NARROW)
  return container || narrow
}

export function useIsField(): boolean {
  return useMedia(FIELD)
}

export function useIsTablet(): boolean {
  return useMedia(TABLET)
}
